import type { AccountRepairView } from '@server/warehouse-repair-state';
import {
  Building2,
  EllipsisVertical,
  Globe,
  type LucideIcon,
  MessageSquareMore,
  PanelRightClose,
  RefreshCw,
  Trash2,
} from 'lucide-react';
import { useCallback, useId, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { NeedsRepairBadge } from '@/_shared/components/AccountRepair/NeedsRepairBadge';
import { ShareSplitButton } from '@/_shared/components/ShareSplitButton';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList } from '@/_shared/components/ui/breadcrumb';
import { Button } from '@/_shared/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/_shared/components/ui/dropdown-menu';
import { Popover, PopoverContent } from '@/_shared/components/ui/popover';
import { useWorkspace } from '@/_shared/contexts/WorkspaceContext';
import { cn } from '@/_shared/lib/utils';

import { usePublicSharingPolicy } from '../hooks/usePublicSharingPolicy';
import type { Dashboard } from '../types';
import { PublishConfirmDialog } from './PublishConfirmDialog';
import { StarToggleButton } from './StarToggleButton';

interface Props {
  dashboard: Dashboard;
  isAdmin?: boolean;
  onToggleChat?: () => void;
  chatOpen?: boolean;
  /** Element id for `aria-controls` on the assistant toggle (desktop vs mobile). */
  chatSidebarId?: string;
  onDelete?: () => void;
  deletePending?: boolean;
  /** Plaintext share token, available only immediately after a mint
   * (publish or regenerate) — the cached dashboard query never carries it. */
  plaintextToken?: string | null;
  onPublish?: () => void;
  onUnpublish?: () => void;
  onRegenerate?: () => void;
  regeneratePending?: boolean;
  /** True while the publish/unpublish mutation is in flight. The Switch is
   * disabled while this is true so a double-click can't kick off two
   * mutations (the second of which would re-open the publish dialog with a
   * stale isPublic). */
  privacyPending?: boolean;
  isStarred?: boolean;
  onToggleStar?: () => void;
  starPending?: boolean;
  /** ACT2-1286: the needs-repair status beside the title (`ok`, and every state with the flag off, shows none). */
  repairState?: AccountRepairView['state'];
}

type DashboardVisibility = 'workspace' | 'public';

interface VisibilityOption {
  value: DashboardVisibility;
  label: string;
  icon: LucideIcon;
  description: string;
}

const OPTIONS: VisibilityOption[] = [
  {
    value: 'workspace',
    label: 'Workspace',
    icon: Building2,
    description: 'Anyone in your workspace can view',
  },
  {
    value: 'public',
    label: 'Anyone with the link',
    icon: Globe,
    description: 'No sign-in required',
  },
];

// No-op fallback for callback props. Hoisted to module scope so its identity is
// stable across renders — passing a freshly-allocated function on every render
// would invalidate any memoization inside SharePopover.
// eslint-disable-next-line @typescript-eslint/no-empty-function -- stable-identity no-op callback for SharePopover
const NOOP = (): void => {};

/**
 * RadioGroup is a child of PopoverContent so its state re-mounts on each
 * popover open. Combined with the "derived from props" guard below, this
 * keeps `focusedIndex` in sync with `currentVisibility` both on popover open
 * and across in-popover visibility mutations (publish / unpublish / refetch).
 *
 * See https://react.dev/reference/react/useState#storing-information-from-previous-renders
 */
function RadioGroup({
  currentVisibility,
  onSelect,
  optionDisabled,
  disabledReasonId,
}: {
  currentVisibility: DashboardVisibility;
  onSelect: (next: DashboardVisibility) => void;
  optionDisabled: Record<DashboardVisibility, boolean>;
  /** DOM id of the visible reason element to wire via aria-describedby for
   *  the public option when it's policy-disabled. Falsy when no reason
   *  applies (admin enabled, or disable is purely transient like pending). */
  disabledReasonId: string | null;
}): React.ReactElement {
  const computeFocusedIndex = (v: DashboardVisibility): number =>
    Math.max(
      0,
      OPTIONS.findIndex((o) => o.value === v),
    );
  const [focusedIndex, setFocusedIndex] = useState(() => computeFocusedIndex(currentVisibility));
  const [prevVisibility, setPrevVisibility] = useState(currentVisibility);
  if (prevVisibility !== currentVisibility) {
    setPrevVisibility(currentVisibility);
    setFocusedIndex(computeFocusedIndex(currentVisibility));
  }

  const optionRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const moveFocus = useCallback((next: number) => {
    setFocusedIndex(next);
    optionRefs.current[next]?.focus();
  }, []);
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent, index: number) => {
      const last = OPTIONS.length - 1;
      if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
        e.preventDefault();
        moveFocus(index === last ? 0 : index + 1);
      } else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
        e.preventDefault();
        moveFocus(index === 0 ? last : index - 1);
      } else if (e.key === 'Home') {
        e.preventDefault();
        moveFocus(0);
      } else if (e.key === 'End') {
        e.preventDefault();
        moveFocus(last);
      }
    },
    [moveFocus],
  );

  return (
    <div className="space-y-1" role="radiogroup" aria-label="Dashboard visibility">
      {OPTIONS.map((opt, index) => {
        const checked = currentVisibility === opt.value;
        const disabled = optionDisabled[opt.value];
        // Only wire aria-describedby on the public option when its disable is
        // policy-driven (a reason element exists). Pending-state disabling has
        // no separate reason element; the row's aria-disabled is enough.
        const describedBy = opt.value === 'public' && disabled && disabledReasonId ? disabledReasonId : undefined;
        return (
          <button
            key={opt.value}
            ref={(el) => {
              optionRefs.current[index] = el;
            }}
            type="button"
            role="radio"
            aria-checked={checked}
            // aria-disabled (not native `disabled`) keeps the row focusable so
            // arrow-key navigation can land on it and screen readers can
            // announce the state + the describedby reason. The onClick guard
            // below handles the no-op behavior.
            aria-disabled={disabled ? 'true' : undefined}
            aria-describedby={describedBy}
            tabIndex={index === focusedIndex ? 0 : -1}
            onClick={() => {
              if (disabled) return;
              onSelect(opt.value);
            }}
            onKeyDown={(e) => handleKeyDown(e, index)}
            className={cn(
              'flex w-full items-center gap-3 rounded-md px-3 py-2 text-left text-sm transition-colors',
              checked ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-muted hover:text-foreground',
              disabled && 'cursor-not-allowed opacity-60',
            )}
            data-testid={`visibility-${opt.value}`}
          >
            <opt.icon className="size-4 shrink-0" />
            <div className="min-w-0">
              <div className="font-medium">{opt.label}</div>
              <div className="text-xs opacity-70">{opt.description}</div>
            </div>
          </button>
        );
      })}
    </div>
  );
}

function SharePopover({
  dashboard,
  plaintextToken,
  onPublish,
  onUnpublish,
  onRegenerate,
  regeneratePending = false,
  privacyPending = false,
}: {
  dashboard: Dashboard;
  plaintextToken: string | null;
  onPublish: () => void;
  onUnpublish: () => void;
  onRegenerate: () => void;
  regeneratePending?: boolean;
  privacyPending?: boolean;
  isStarred?: boolean;
  onToggleStar?: () => void;
  starPending?: boolean;
}): React.ReactElement {
  const { publicSharingEnabled } = usePublicSharingPolicy();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const publicUrl = plaintextToken ? `${window.location.origin}/pub/${plaintextToken}` : '';
  // Workspace URL = the full address bar the user is on, including time-range
  // and filter query params. We keep them so the recipient lands on the same
  // view; chat's share URL is also a canonical URL, so this stays consistent.
  const workspaceUrl = typeof window === 'undefined' ? '' : window.location.href;
  // Treat a freshly-minted token as "public" even before the dashboard query
  // refetch lands — otherwise the trigger would still say "workspace" and the
  // copy segment would copy the workspace URL in the brief window after the
  // publish mutation succeeds.
  const effectivelyPublic = dashboard.isPublic || !!plaintextToken;
  const currentVisibility: DashboardVisibility = effectivelyPublic ? 'public' : 'workspace';
  const isPublicWithLiveToken = effectivelyPublic && publicSharingEnabled && !!plaintextToken;
  const shareUrl = isPublicWithLiveToken ? publicUrl : workspaceUrl;
  // State D: dashboard is public but admin disabled public sharing OR the
  // plaintext token isn't loaded (popover was closed). Suppress the copy
  // segment so the trigger doesn't ship a dead-link.
  const noCopy = effectivelyPublic && (!publicSharingEnabled || !plaintextToken);

  const handleSelect = useCallback(
    (next: DashboardVisibility): void => {
      if (next === currentVisibility) return;
      if (next === 'public') {
        // Confirm before going public — the dialog spells out the "leaves the
        // workspace + may be indexed" implications before the mutation fires.
        setConfirmOpen(true);
      } else {
        onUnpublish();
      }
    },
    [currentVisibility, onUnpublish],
  );

  const handleConfirm = useCallback((): void => {
    setConfirmOpen(false);
    onPublish();
  }, [onPublish]);

  // Public option is disabled when the user can NEITHER publish (admin-off
  // gate) NOR is the dashboard already public (in which case the user must
  // be allowed to keep it selected). Workspace option is also disabled while
  // a regenerate is in flight: otherwise a user who clicks Workspace mid-
  // regenerate hits a race where the regenerate response lands AFTER the
  // unpublish, leaving `plaintextToken` populated and the trigger advertising
  // a `/pub/...` URL that 404s.
  const publicDisabled = (!publicSharingEnabled && !dashboard.isPublic) || privacyPending || regeneratePending;
  const workspaceDisabled = privacyPending || regeneratePending;
  const optionDisabled: Record<DashboardVisibility, boolean> = {
    workspace: workspaceDisabled,
    public: publicDisabled,
  };
  // The reason element for the policy-driven disable of the public option.
  // Wired via aria-describedby on the public radio so screen readers
  // announce the cause when focus lands on it.
  const policyReasonId = useId();
  const showPolicyReason = !publicSharingEnabled && !dashboard.isPublic;

  return (
    <>
      <Popover>
        <ShareSplitButton visibility={currentVisibility} shareUrl={shareUrl} noCopy={noCopy} />
        <PopoverContent className="w-80" align="end">
          <div className="space-y-3">
            <p className="text-sm font-medium text-foreground">Share this dashboard</p>
            <RadioGroup
              currentVisibility={currentVisibility}
              onSelect={handleSelect}
              optionDisabled={optionDisabled}
              disabledReasonId={showPolicyReason ? policyReasonId : null}
            />

            {showPolicyReason && (
              <p id={policyReasonId} className="text-xs text-muted-foreground">
                Public sharing is disabled by your workspace admin.
              </p>
            )}

            {dashboard.isPublic && !publicSharingEnabled && (
              <div className="rounded-md border border-warning-border bg-warning-bg px-3 py-2 text-xs text-warning">
                This link is currently inaccessible. An admin disabled public sharing for this workspace. Switch to
                Workspace to clear the existing token.
              </div>
            )}

            {dashboard.isPublic && publicSharingEnabled && plaintextToken && (
              <div className="space-y-2">
                <label className="text-xs text-muted-foreground">Share link</label>
                <input
                  readOnly
                  value={publicUrl}
                  className="w-full truncate rounded-md border border-border bg-muted px-3 py-1.5 font-mono text-xs text-foreground"
                  onFocus={(e) => e.target.select()}
                />
                <Button
                  variant="tertiary"
                  className="w-full gap-2 text-xs"
                  onClick={onRegenerate}
                  disabled={regeneratePending}
                  Icon={<RefreshCw />}
                  label="Generate a new link"
                />
              </div>
            )}

            {dashboard.isPublic && publicSharingEnabled && !plaintextToken && (
              <div className="space-y-2">
                <p className="text-xs text-muted-foreground">
                  The share link is hidden after the popover closes. Generate a new link to share again.
                </p>
                <Button
                  variant="tertiary"
                  className="w-full gap-2 text-xs"
                  onClick={onRegenerate}
                  disabled={regeneratePending}
                  Icon={<RefreshCw />}
                  label="Generate a new link"
                />
              </div>
            )}
          </div>
        </PopoverContent>
      </Popover>

      <PublishConfirmDialog open={confirmOpen} onConfirm={handleConfirm} onCancel={() => setConfirmOpen(false)} />
    </>
  );
}

export function DashboardHeader({
  dashboard,
  isAdmin = false,
  onToggleChat,
  chatOpen = false,
  chatSidebarId = 'dashboard-chat-sidebar',
  onDelete,
  deletePending,
  plaintextToken = null,
  onPublish,
  onUnpublish,
  onRegenerate,
  regeneratePending,
  privacyPending,
  isStarred = false,
  onToggleStar,
  starPending = false,
  repairState = 'ok',
}: Props): React.ReactElement {
  // The share popover is gated on isAdmin. The Dashboard view above us is
  // already passing isAdmin = (workspaceAdmin || dashboard.createdBy === user.id),
  // so creators-without-admin still get the popover from this single flag.
  // Callbacks default to a stable module-scope NOOP so tests / read-only renders
  // still mount the popover; the parent wires real mutations on Dashboard.tsx.
  const canShare = isAdmin;
  const { wp } = useWorkspace();

  return (
    <div className="border-b border-border px-6 py-4">
      <Breadcrumb className="mb-4">
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to={wp('/dashboards')}>Dashboards</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between md:gap-0">
        <div className="min-w-0">
          <div className="flex min-w-0 items-center gap-2">
            <h1 className="truncate text-lg font-semibold text-foreground">{dashboard.title}</h1>
            <NeedsRepairBadge state={repairState} withTooltip className="shrink-0" />
            {onToggleStar && (
              <StarToggleButton
                dashboardTitle={dashboard.title}
                starred={isStarred}
                loading={starPending}
                onToggle={onToggleStar}
                className="shrink-0"
              />
            )}
          </div>
          {dashboard.description && (
            <p className="mt-0.5 line-clamp-2 text-sm text-muted-foreground">{dashboard.description}</p>
          )}
        </div>

        <div className="flex shrink-0 items-center gap-2 self-end md:ml-4 md:self-auto">
          {canShare && (
            <SharePopover
              dashboard={dashboard}
              plaintextToken={plaintextToken}
              onPublish={onPublish ?? NOOP}
              onUnpublish={onUnpublish ?? NOOP}
              onRegenerate={onRegenerate ?? NOOP}
              regeneratePending={regeneratePending}
              privacyPending={privacyPending}
            />
          )}

          {onToggleChat && (
            <Button
              variant="secondary"
              className="gap-2"
              onClick={onToggleChat}
              aria-label={chatOpen ? 'Close dashboard assistant' : 'Ask about this dashboard'}
              aria-expanded={chatOpen}
              aria-controls={chatSidebarId}
              title="Ask about this dashboard (⌘⇧K)"
              Icon={
                chatOpen ? <PanelRightClose className="h-3.5 w-3.5" /> : <MessageSquareMore className="h-3.5 w-3.5" />
              }
              label={chatOpen ? 'Close' : 'Ask'}
            />
          )}

          {isAdmin && onDelete && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="secondary" aria-label="Dashboard actions" Icon={<EllipsisVertical />} />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem variant="destructive" disabled={deletePending} onClick={() => onDelete()}>
                  <Trash2 className="mr-2 size-3.5" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>
    </div>
  );
}
